import "server-only"
import axios from "axios"
import { load } from "cheerio"
import { cacheLife } from "next/cache"
import { cache } from "react"
import { env } from "@/env"
import { wordPressApi } from "./api"

export interface WordPressStoreUser {
  id: number
  name: string
  slug: string
  url?: string
}

export const getStoreUserBySlug = cache(
  async (storeSlug: string): Promise<WordPressStoreUser | null> => {
    "use cache"

    cacheLife("hours")

    const normalizedSlug = storeSlug.trim()
    if (!normalizedSlug) {
      return null
    }

    const { data } = await wordPressApi.get<WordPressStoreUser[]>(
      "/wp/v2/users",
      {
        params: {
          slug: normalizedSlug,
        },
      }
    )

    const matchedStore =
      data.find((store) => store.slug === normalizedSlug) ?? data[0]

    return matchedStore ?? null
  }
)

export interface WordPressUser {
  expiry: Date
  username: string
}

/**
 * Extracts valid (non-expired, non-cleared) Set-Cookie entries from an axios response.
 * Accepts the `set-cookie` header array from axios response headers.
 */
export const extractValidWpCookies = (
  setCookieHeaders: string[]
): string | null => {
  const valid = setCookieHeaders
    .filter((header) => {
      if (header.includes("Max-Age=0")) return false
      const value = header.split(";")[0]?.split("=").slice(1).join("=") ?? ""
      if (value === "%20" || value === "") return false
      return true
    })
    .map((header) => header.split(";")[0])
    .filter(Boolean)

  return valid.length > 0 ? valid.join("; ") : null
}

/**
 * Decodes the wordpress_logged_in_* cookie value.
 * Format: username|expiry|token|hmac
 * Returns the username (WP login / email) and expiry date.
 */
export const decodeWordPressLoggedInCookie = (
  cookieString: string
): WordPressUser | null => {
  try {
    // find the wordpress_logged_in_* cookie value
    const match = cookieString
      .split("; ")
      .find((part) => part.startsWith("wordpress_logged_in_"))

    if (!match) return null

    const cookieValue = match.split("=").slice(1).join("=")
    const decoded = decodeURIComponent(cookieValue)
    const parts = decoded.split("|")

    if (parts.length < 2) return null

    const username = parts[0]
    const expiryTimestamp = Number.parseInt(parts[1] ?? "0", 10)

    if (!username || Number.isNaN(expiryTimestamp)) return null

    return {
      username,
      expiry: new Date(expiryTimestamp * 1000),
    }
  } catch {
    return null
  }
}

export const postAdminAjax = async (
  endpoint: string,
  body: URLSearchParams
): Promise<{ data: unknown; cookies: string | null }> => {
  const response = await axios.post(endpoint, body.toString(), {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  })

  const cookies = extractValidWpCookies(response.headers["set-cookie"] ?? [])
  const data = response.data

  return { data, cookies }
}

/**
 * Same as postAdminAjax but forwards the user's WP session cookie.
 * Use this for wp_ajax_ actions (not nopriv) that call get_current_user_id()
 * or is_user_logged_in() inside the callback.
 *
 * The sessionCookie is the raw WP cookie string stored as session.wordpressSessionCookie,
 * e.g. "wordpress_logged_in_xxx=val; wordpress_sec_xxx=val".
 */
export const postAdminAjaxAsUser = async (
  endpoint: string,
  body: URLSearchParams,
  sessionCookie: string
): Promise<{ data: unknown; cookies: string | null }> => {
  const response = await axios.post(endpoint, body.toString(), {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Cookie: sessionCookie,
    },
  })

  const cookies = extractValidWpCookies(response.headers["set-cookie"] ?? [])
  const data = response.data

  return { data, cookies }
}

export type AccountAddressMode = "billing" | "shipping"

export interface WordPressAddressBlock {
  address_1?: string
  address_2?: string
  billing_cpf?: string
  billing_neighborhood?: string
  billing_number?: string
  billing_phone?: string
  city?: string
  company?: string
  country?: string
  cpf?: string
  email?: string
  first_name?: string
  last_name?: string
  neighborhood?: string
  number?: string
  phone?: string
  postcode?: string
  shipping_cpf?: string
  shipping_neighborhood?: string
  shipping_number?: string
  shipping_phone?: string
  state?: string
}

export interface WordPressAccountAddresses {
  billing?: WordPressAddressBlock
  shipping?: WordPressAddressBlock
}

export interface WordPressAddressUpdatePayload {
  billing?: WordPressAddressBlock
  shipping?: WordPressAddressBlock
}

export interface AccountDetailsData {
  displayName: string
  email: string
  firstName: string
  lastName: string
}

export interface EditAccountFormSnapshot {
  details: AccountDetailsData
  html: string
  nonce: string | null
  notices: string[]
}

export interface EditAccountSubmitResult {
  html: string
  notices: string[]
  success: boolean
}

export interface WordPressMyOrderLineItem {
  product_id?: number | string
  product_image?: string
  product_permalink?: string
  quantity?: number | string
}

export interface WordPressMyOrderItem {
  currency?: string
  date_created?: string
  id: number | string
  items?: WordPressMyOrderLineItem[] | Record<string, WordPressMyOrderLineItem>
  status: string
  total: number | string
}

export interface WordPressMyOrdersResponse {
  data?: WordPressMyOrderItem[]
  message?: string
  success?: boolean
}

interface WordPressAccountAddressesWrappedResponse {
  data?: WordPressAccountAddresses
  success?: boolean
}

const getAccountAddressesEndpoint = (): string =>
  `${new URL(env.WP_API_URL).origin}/wp-json/triibo/v1/addresses`

const getEditAccountEndpoint = (): string =>
  `${new URL(env.WP_API_URL).origin}/minha-conta/edit-account/`

const getMyOrdersEndpoint = (): string =>
  `${new URL(env.WP_API_URL).origin}/wp-json/triibo/v1/my-orders`

const getWordPressRestNonceEndpoint = (): string =>
  `${new URL(env.WP_API_URL).origin}/wp-admin/admin-ajax.php`

const getWordPressRestNonce = async (
  sessionCookie: string
): Promise<string | null> => {
  const { data } = await axios.get<string>(getWordPressRestNonceEndpoint(), {
    params: {
      action: "rest-nonce",
    },
    headers: {
      Cookie: sessionCookie,
    },
    responseType: "text",
  })

  const nonce =
    typeof data === "string" ? data.trim() : String(data ?? "").trim()

  return nonce.length > 0 ? nonce : null
}

const buildWordPressAuthenticatedHeaders = async (
  sessionCookie: string
): Promise<Record<string, string>> => {
  const restNonce = await getWordPressRestNonce(sessionCookie)
  const headers: Record<string, string> = {
    Cookie: sessionCookie,
  }

  if (restNonce) {
    headers["X-WP-Nonce"] = restNonce
  }

  return headers
}

const unwrapWordPressAddressesResponse = (
  response: WordPressAccountAddresses | WordPressAccountAddressesWrappedResponse
): WordPressAccountAddresses => {
  if (
    response &&
    typeof response === "object" &&
    "success" in response &&
    "data" in response
  ) {
    const wrappedResponse = response as WordPressAccountAddressesWrappedResponse

    return wrappedResponse.data ?? {}
  }

  return response as WordPressAccountAddresses
}

const normalizeNoticeText = (value: string): string =>
  value.replace(/\s+/g, " ").trim()

const parseWooNotices = (html: string): string[] => {
  const $ = load(html)
  const noticeItems = $(
    ".woocommerce-error li, .woocommerce-info li, .woocommerce-message li"
  )
    .toArray()
    .map((item) => normalizeNoticeText($(item).text()))
    .filter((item) => item.length > 0)

  if (noticeItems.length > 0) {
    return noticeItems
  }

  return $(".woocommerce-error, .woocommerce-info, .woocommerce-message")
    .toArray()
    .map((item) => normalizeNoticeText($(item).text()))
    .filter((item) => item.length > 0)
}

export const fetchEditAccountFormSnapshot = async (
  sessionCookie: string
): Promise<EditAccountFormSnapshot> => {
  const { data } = await axios.get<string>(getEditAccountEndpoint(), {
    headers: {
      Cookie: sessionCookie,
    },
    responseType: "text",
  })

  const html = typeof data === "string" ? data : String(data ?? "")
  const $ = load(html)

  return {
    nonce:
      $("input[name='save-account-details-nonce']")
        .first()
        .attr("value")
        ?.trim() ?? null,
    details: {
      firstName: $("#account_first_name").first().attr("value")?.trim() ?? "",
      lastName: $("#account_last_name").first().attr("value")?.trim() ?? "",
      displayName:
        $("#account_display_name").first().attr("value")?.trim() ?? "",
      email: $("#account_email").first().attr("value")?.trim() ?? "",
    },
    notices: parseWooNotices(html),
    html,
  }
}

export const submitEditAccount = async (
  sessionCookie: string,
  payload: URLSearchParams
): Promise<EditAccountSubmitResult> => {
  const { data } = await axios.post<string>(
    getEditAccountEndpoint(),
    payload.toString(),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        Cookie: sessionCookie,
      },
      responseType: "text",
    }
  )

  const html = typeof data === "string" ? data : String(data ?? "")
  const notices = parseWooNotices(html)
  const $ = load(html)
  const hasErrorNotice = $(".woocommerce-error").length > 0
  const hasSuccessNotice = $(".woocommerce-message").length > 0

  return {
    success: hasSuccessNotice && !hasErrorNotice,
    notices,
    html,
  }
}

export const getWordPressAccountOrders = async (
  sessionCookie: string,
  limit = 20
): Promise<WordPressMyOrderItem[]> => {
  const headers = await buildWordPressAuthenticatedHeaders(sessionCookie)

  const { data } = await axios.get<WordPressMyOrdersResponse>(
    getMyOrdersEndpoint(),
    {
      headers,
      params: {
        limit,
      },
    }
  )

  if (!data.success) {
    return []
  }

  return Array.isArray(data.data) ? data.data : []
}

export const getAccountAddresses = async (
  sessionCookie: string
): Promise<WordPressAccountAddresses> => {
  const headers = await buildWordPressAuthenticatedHeaders(sessionCookie)

  const { data } = await axios.get<
    WordPressAccountAddresses | WordPressAccountAddressesWrappedResponse
  >(getAccountAddressesEndpoint(), {
    headers,
  })

  return unwrapWordPressAddressesResponse(data)
}

export const updateAccountAddress = async (
  sessionCookie: string,
  mode: AccountAddressMode,
  payload: WordPressAddressUpdatePayload
): Promise<WordPressAccountAddresses> => {
  const headers = await buildWordPressAuthenticatedHeaders(sessionCookie)
  const modePayload: WordPressAddressUpdatePayload =
    mode === "billing"
      ? {
          billing: payload.billing ?? {},
        }
      : {
          shipping: payload.shipping ?? {},
        }

  const { data } = await axios.post<
    WordPressAccountAddresses | WordPressAccountAddressesWrappedResponse
  >(getAccountAddressesEndpoint(), modePayload, {
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  })

  return unwrapWordPressAddressesResponse(data)
}

export interface WordPressOrderItem {
  id: number
  image?: {
    src?: string
  }
  meta_data?: Array<{
    key: string
    value: string
    display_key?: string
    display_value?: string
  }>
  name: string
  product_id?: number
  product_permalink?: string
  quantity: number | string
  sku?: string
  subtotal: string | number
  total: string | number
}

export interface WordPressOrderAddress {
  address_1: string
  address_2?: string
  city: string
  company?: string
  country: string
  email?: string
  first_name: string
  last_name: string
  phone?: string
  postcode: string
  state: string
}

export interface WordPressOrderNote {
  customer_note: boolean
  date_created: string
  id: number
  note: string
}

export interface WordPressOrderDetail {
  billing: WordPressOrderAddress
  currency: string
  customer_note?: string
  customer_order_notes?: WordPressOrderNote[]
  date_created: string
  discount_total: string | number
  id: number | string
  line_items: WordPressOrderItem[]
  needs_shipping_address: boolean
  number: string
  shipping: WordPressOrderAddress
  shipping_total: string | number
  status: string
  subtotal: string | number
  total: string | number
  total_tax: string | number
}

interface TriiboOrderDetailResponse {
  data: {
    id: number
    number: string
    status: string
    date_created: string
    currency: string
    items: WordPressOrderItem[]
    subtotal: string | number
    total: string | number
    tax_total: string | number
    shipping_total: string | number
    discount_total: string | number
    billing_address: WordPressOrderAddress & {
      number?: string
      neighborhood?: string
    }
    shipping_address: WordPressOrderAddress & {
      number?: string
      neighborhood?: string
    }
    customer_note?: string
    customer_order_notes?: WordPressOrderNote[]
  }
  success: boolean
}

export async function getWordPressOrderDetail(
  sessionCookie: string,
  orderNumber: string
): Promise<WordPressOrderDetail> {
  const headers = await buildWordPressAuthenticatedHeaders(sessionCookie)

  const url = `${new URL(env.WP_API_URL).origin}/wp-json/triibo/v1/my-orders/${orderNumber}`

  const response = await axios.get<TriiboOrderDetailResponse>(url, {
    headers,
  })

  const triiboData = response.data.data

  // Transform Triibo format to WordPressOrderDetail format
  return {
    id: triiboData.id,
    number: triiboData.number,
    status: triiboData.status,
    date_created: triiboData.date_created,
    currency: triiboData.currency,
    line_items: triiboData.items,
    subtotal: triiboData.subtotal,
    total: triiboData.total,
    total_tax: triiboData.tax_total,
    shipping_total: triiboData.shipping_total,
    discount_total: triiboData.discount_total,
    billing: triiboData.billing_address,
    shipping: triiboData.shipping_address,
    customer_note: triiboData.customer_note,
    customer_order_notes: triiboData.customer_order_notes,
    needs_shipping_address: !!triiboData.shipping_address?.address_1,
  }
}
